import { Link } from "react-router-dom";
import {
  CardMedia,
  Paper,
  Stack,
  Typography,
  Box,
  Grid2,
  IconButton,
} from "@mui/material";
import { OpenInNew } from "@mui/icons-material";
import { useTranslation } from "../../../hooks/useTranslation";
import { useCurrency } from "../../../hooks/useCurrency";
import { DimensionInformation } from "../../product/components/types";
import { OrderItem } from "../../orders/utils/types";
import { ReactNode } from "react";
import { Copyable } from "../../../components/copyable/Copyable";

const InfoRow = ({
  label,
  value,
  color,
}: {
  label: string;
  value: ReactNode;
  color?: string;
}) => (
  <Stack direction="row" justifyContent="space-between">
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={500} color={color}>
      {value}
    </Typography>
  </Stack>
);

type OrderItemCardProps = {
  orderItem: OrderItem;
  orderStatus: string;
  warehouseErrorMessage?: string;
};

export const OrderItemCard = ({
  orderItem,
  orderStatus,
  warehouseErrorMessage,
}: OrderItemCardProps) => {
  const { translate, translateWithoutPrefix } = useTranslation(
    "pages.orders.detail",
  );
  const { formatCurrency } = useCurrency();

  const purchaseStatus =
    orderItem.productDetails?.storeProductStatus === "PENDING_APPROVE"
      ? translate("orderItem.notPurchased")
      : translate("orderItem.purchased");

  const getDimensionText = (dimension: DimensionInformation | undefined) => {
    if (!dimension?.value) return translateWithoutPrefix("common.unknown");
    return `${dimension.value}`;
  };

  const getWHLText = () => {
    const product = orderItem.productDetails?.marketplaceProduct?.product;

    if (!product?.width || !product?.height || !product?.length)
      return translateWithoutPrefix("common.unknown");

    const unit = product.width.unit; // hepsi aynı unit varsayımı

    return `${getDimensionText(product.width)} × ${getDimensionText(
      product.height,
    )} × ${getDimensionText(product.length)} ${unit}`;
  };

  const renderWarehouseError = (message?: string) => {
    if (!message) return null;

    return (
      <Stack spacing={0.25}>
        {message
          .split(";")
          .map((m) => m.trim())
          .filter(Boolean)
          .map((m) => (
            <Typography key={m} variant="body2" color="error">
              • {m.charAt(0).toUpperCase() + m.slice(1)}
            </Typography>
          ))}
      </Stack>
    );
  };

  return (
    <Paper sx={{ mb: 2, p: 1.5 }}>
      <Stack direction="row" spacing={2}>
        {/* Product Image */}
        <CardMedia
          component="img"
          src={orderItem.productDetails?.productImageUrl}
          alt={orderItem.productDetails?.marketplaceProduct?.itemName}
          sx={{
            width: 128,
            height: "100%",
            objectFit: "contain",
            alignSelf: "center",
          }}
        />

        {/* Content */}
        <Stack flex={1} spacing={1}>
          {/* Title */}
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography
              component={Link}
              to={`/inventory/product/${orderItem.productDetails?.asin?.toUpperCase()}`}
              target="_blank"
              rel="noopener noreferrer"
              variant="body1"
              sx={{ textDecoration: "none" }}
              fontWeight={500}
            >
              {orderItem.productDetails?.marketplaceProduct?.itemName ??
                translateWithoutPrefix("common.unknown")}
            </Typography>

            <IconButton
              component={Link}
              to={`https://www.amazon.com/dp/${orderItem.productDetails?.asin}`}
              target="_blank"
              rel="noopener noreferrer"
              color="primary"
            >
              <OpenInNew fontSize="small" />
            </IconButton>
          </Stack>

          <Grid2 container spacing={2}>
            <Grid2
              component={Paper}
              variant="outlined"
              size={{ xs: 12, md: 4 }}
              sx={{ p: 1.5, height: "100%" }}
            >
              <Typography variant="subtitle2" fontWeight={600} pb={1}>
                {translate("orderItem.productDetail")}
              </Typography>

              <Stack spacing={0.5}>
                <InfoRow
                  label={translate("orderItem.orderedQuantity")}
                  value={orderItem.quantityOrdered}
                />
                <InfoRow
                  label={translate("orderItem.quantityBought")}
                  value={orderItem.quantityBought ?? 0}
                  color={
                    (orderItem.quantityBought ?? 0) === 0
                      ? "error.main"
                      : undefined
                  }
                />

                <InfoRow
                  label={translate("orderItem.price")}
                  value={formatCurrency(orderItem.itemPrice)}
                  color="success.main"
                />

                {orderItem.itemTax > 0 && (
                  <InfoRow
                    label={translate("orderItem.tax")}
                    value={formatCurrency(orderItem.itemTax)}
                    color="error.main"
                  />
                )}
                {orderItem.promotionDiscount > 0 && (
                  <InfoRow
                    label={translate("orderItem.promotionDiscount")}
                    value={formatCurrency(
                      orderItem.promotionDiscount,
                      "FORCE_LOCAL",
                    )}
                    color="error.main"
                  />
                )}
                <InfoRow
                  label={translate("orderItem.productStatus")}
                  value={purchaseStatus}
                  color={
                    orderItem.productDetails?.storeProductStatus ===
                    "PENDING_APPROVE"
                      ? "error.main"
                      : "success.main"
                  }
                />
              </Stack>
            </Grid2>
            <Grid2
              component={Paper}
              variant="outlined"
              size={{ xs: 12, md: 4 }}
              sx={{ p: 1.5, height: "100%" }}
            >
              <Typography variant="subtitle2" fontWeight={600} pb={1}>
                {translate("orderItem.physicalInfo")}
              </Typography>
              <Stack spacing={0.5}>
                <InfoRow
                  label={translate("orderItem.dimensions")}
                  value={getWHLText()}
                />
                <InfoRow
                  label={translate("orderItem.weight")}
                  value={
                    orderItem.productDetails?.marketplaceProduct?.product
                      ?.weight?.value
                      ? `${orderItem.productDetails.marketplaceProduct.product.weight.value} ${orderItem.productDetails.marketplaceProduct.product.weight.unit}`
                      : translateWithoutPrefix("common.unknown")
                  }
                />

                <InfoRow
                  label={translate("desi")}
                  value={
                    orderItem.productDetails?.marketplaceProduct?.product
                      ?.desi ?? translateWithoutPrefix("common.unknown")
                  }
                />
              </Stack>
            </Grid2>
            <Grid2
              component={Paper}
              variant="outlined"
              size={{ xs: 12, md: 4 }}
              sx={{ p: 1.5, height: "100%" }}
            >
              <Typography variant="subtitle2" fontWeight={600} pb={1}>
                {translate("orderItem.supplier")}
              </Typography>

              <Stack spacing={0.5}>
                <InfoRow
                  label={translate("orderItem.supplier")}
                  value={
                    orderItem.productDetails?.supplier?.supplierName ??
                    translateWithoutPrefix("common.unknown")
                  }
                />
                <InfoRow
                  label={translate("orderItem.stock")}
                  value={orderItem.productDetails?.supplier?.stock ?? 0}
                  color={
                    (orderItem.productDetails?.supplier?.stock ?? 0) === 0
                      ? "error.main"
                      : undefined
                  }
                />

                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  justifyContent={"space-between"}
                >
                  <Typography variant="body2" color="text.secondary">
                    ASIN
                  </Typography>
                  <Box>
                    <Copyable
                      initialLabel={
                        orderItem.productDetails?.asin?.toUpperCase() ?? ""
                      }
                    />
                  </Box>
                </Stack>
              </Stack>
            </Grid2>
          </Grid2>

          <Stack direction={"row"} sx={{ mt: 1 }} spacing={1}>
            <Typography
              variant="body2"
              fontWeight={700}
              color={
                orderStatus === "WAREHOUSE_ERROR"
                  ? "error.main"
                  : "warning.main"
              }
            >
              {translate("orderItem.purchaseStatus")}:
            </Typography>

            {orderStatus === "WAREHOUSE_ERROR" ? (
              renderWarehouseError(warehouseErrorMessage)
            ) : (
              <Typography variant="body2" fontWeight={600}>
                {translate("status." + orderStatus)}
              </Typography>
            )}
          </Stack>
        </Stack>
      </Stack>
    </Paper>
  );
};
